import type {
  LoanIdentityAnswer,
  LoanIdentityExistingLoan,
  LoanIdentityHeldLoan,
  LoanIdentityHoldState,
  LoanIdentityQueueRow,
  LoanIdentityVerdict,
} from './loan-identity-queue-row.type.ts';

type Wire = Record<string, unknown>;

function isRecord(value: unknown): value is Wire {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Wire, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' ? value : null;
}

function readNumber(record: Wire, key: string): number | null {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readBoolean(record: Wire, key: string): boolean | null {
  const value = record[key];
  return typeof value === 'boolean' ? value : null;
}

const HOLD_STATES: readonly LoanIdentityHoldState[] = ['held', 'answered', 'applied', 'discarded'];
const VERDICTS: readonly LoanIdentityVerdict[] = ['same', 'different'];

function readHeldLoan(value: unknown): LoanIdentityHeldLoan {
  const record = isRecord(value) ? value : {};
  return {
    name: readString(record, 'name'),
    rawType: readString(record, 'raw-type'),
    type: readString(record, 'type'),
    typeLabel: readString(record, 'type-label'),
    disbursedOn: readString(record, 'disbursed-on'),
    originalAmount: readNumber(record, 'original-amount'),
    currentBalance: readNumber(record, 'current-balance'),
    servicerName: readString(record, 'servicer-name'),
  };
}

function readExistingLoan(record: Wire): LoanIdentityExistingLoan | null {
  const loanId = readString(record, 'loan-id');
  if (!loanId) return null;
  return {
    loanId,
    name: readString(record, 'name'),
    type: readString(record, 'type'),
    typeLabel: readString(record, 'type-label'),
    disbursedOn: readString(record, 'disbursed-on'),
    originalAmount: readNumber(record, 'original-amount'),
    currentBalance: readNumber(record, 'current-balance'),
    inUse: record['in-use'] === true,
    score: readNumber(record, 'score'),
    isSame: readBoolean(record, 'is-same'),
    becomesBalance: readNumber(record, 'becomes-balance'),
  };
}

function readAnswer(value: unknown): LoanIdentityAnswer {
  const record = isRecord(value) ? value : {};
  const state = readString(record, 'state');
  const verdict = readString(record, 'verdict');
  return {
    holdId: readString(record, 'hold-id') ?? '',
    resolutionId: readString(record, 'resolution-id') ?? '',
    state: HOLD_STATES.find((each) => each === state) ?? null,
    verdict: VERDICTS.find((each) => each === verdict) ?? null,
    answeredLoanId: readString(record, 'answered-loan-id'),
    appliedLoanId: readString(record, 'applied-loan-id'),
  };
}

/**
 * Read-only projection of a `LoanIdentityQueueRow` — the admin Loan Identity
 * Match queue's row, backed by `GET /api/v2/loan-identity-queue-rows`.
 *
 * The nested members are read here from the kit's dasherized keys into
 * camelCase shapes, so no caller reads the wire spelling. The person and the
 * upload are flat fields, projected into the one-element lists the shared
 * provenance cells render.
 */
export interface LoanIdentityQueueRowExtension extends LoanIdentityQueueRow {}
export class LoanIdentityQueueRowExtension {
  get isPending() {
    return this.status === 'pending';
  }

  get isResolvedByUser() {
    return this.status === 'resolved_by_user';
  }

  get isResolvedByTio() {
    return this.status === 'resolved_by_tio';
  }

  get heldLoanDetails(): LoanIdentityHeldLoan {
    return readHeldLoan(this.heldLoan);
  }

  /** The account's kept loans in the server's order; an entry without a loan id is dropped. */
  get existingLoanDetails(): LoanIdentityExistingLoan[] {
    const entries = Array.isArray(this.existingLoans) ? this.existingLoans : [];
    return entries
      .filter(isRecord)
      .map(readExistingLoan)
      .filter((loan): loan is LoanIdentityExistingLoan => loan !== null);
  }

  get answerDetails(): LoanIdentityAnswer {
    return readAnswer(this.answer);
  }

  /** The id `complete-on-behalf` decisions name this held loan by. */
  get holdId() {
    return this.answerDetails.holdId;
  }

  /** Whether the held loan still waits on an answer; only then may PayOps complete it. */
  get isAnswerable() {
    return this.isPending && this.answerDetails.state === 'held';
  }

  /** The loan the answer landed on, among the account's loans; undefined until applied. */
  get appliedLoan() {
    const id = this.answerDetails.appliedLoanId;
    return id ? this.existingLoanDetails.find((loan) => loan.loanId === id) : undefined;
  }

  /** The row's owner, shaped as the one-element list the shared people cell renders. */
  get provenancePeople() {
    if (!this.personId) return [];
    return [{ id: this.personId, name: this.personName ?? null }];
  }

  /** Always 0 — a row carries its whole owner, not a sample. */
  get hiddenPeopleCount() {
    return 0;
  }

  /** The row's upload, shaped as the one-element list the shared documents cell renders. */
  get provenanceSourceDocuments() {
    if (!this.documentId) return [];
    return [
      {
        id: this.documentId,
        provider: this.documentProvider,
        provided_at: this.documentUploadedAt ?? null,
        person_id: this.personId,
      },
    ];
  }

  /** Always 0 — a row carries its whole upload, not a sample. */
  get hiddenSourceDocumentsCount() {
    return 0;
  }
}

const Registration = {
  kind: 'object',
  name: 'loan-identity-queue-row',
  features: LoanIdentityQueueRowExtension,
};
export default Registration;
